import React from 'react';
import { connect } from 'react-redux';
import Style from './DataControlEntity.module.css';

// Browser order is fixed rather than taken from the data, so the columns line up
// across rows even when an extension has no build for one of them. Safari is absent
// deliberately: it is out of scope, and its lookup rows are seeded inactive.
const BROWSERS = [
  { code: 'CHROME', label: 'Chrome' },
  { code: 'FIREFOX', label: 'Firefox' },
  { code: 'EDGE', label: 'Edge' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Extensions — the seeded global extension catalogue and its native-id map.
//
// READ-ONLY, and structurally so: this data is maintained by population scripts and
// the endpoint serving it has no POST/PUT/DELETE at all. There is no edit affordance
// here because there is no write path to attach one to.
//
// WHY THIS SCREEN MATTERS. The map rows are the only thing recording that a
// different native id in each browser is the SAME extension. That is what lets one
// predefined rule cover an extension everywhere. An extension missing from this
// catalogue is "uncatalogued": it still shows up in the fleet inventory, but it
// appears once per browser and no predefined rule can be attached to it. So a gap
// here is the reason an extension cannot be governed — worth being able to see.
//
// The predefined extension RULES are not listed here: they are ordinary seeded
// MST_SCAN_RULE rows and already appear on the Scan Rules tab.
// ─────────────────────────────────────────────────────────────────────────────
const Extensions = ({ catalogue, loading }) => {
  const rows = Array.isArray(catalogue) ? catalogue : [];

  const nativeIdFor = (row, browserCode) => {
    const hit = (row.browsers || []).find((b) => b.browserCode === browserCode);
    return hit ? hit.nativeExtensionId : null;
  };

  // An entry with no map rows at all is still shown, not filtered out: a rule
  // targeting it could never match anything, and the attach API refuses it — so
  // this is exactly the misconfiguration an operator needs to notice.
  const unmapped = rows.filter((r) => !(r.browsers || []).length).length;

  return (
    <div className={Style.card}>
      <div className={Style.cardHeader}>
        <div className={Style.cardTitle}>Extension Catalogue</div>
        <div className={Style.cardSubtitle}>
          Extensions PIXM recognises, with the id each browser assigns them. Maintained by database
          release — read-only here. An extension listed with no browser ids cannot be governed until
          they are added.
        </div>
      </div>
      <div className={Style.cardBody}>
        {unmapped > 0 && (
          <div className={Style.statNote}>
            {unmapped} catalogue {unmapped === 1 ? 'entry has' : 'entries have'} no browser ids, so no
            rule can match {unmapped === 1 ? 'it' : 'them'} yet.
          </div>
        )}
        <table className={Style.table}>
          <thead>
            <tr>
              <th>Extension</th>
              <th>Publisher</th>
              <th>Governing Rule</th>
              {BROWSERS.map((b) => <th key={b.code}>{b.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={3 + BROWSERS.length} style={{ textAlign: 'center', padding: '24px' }}>
                  No catalogued extensions
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.logicalExtensionId}>
                <td>{r.extensionName || r.logicalExtensionId}</td>
                <td>{r.publisher || <span className={Style.rowDesc}>—</span>}</td>
                <td>{r.scanRuleName || <span className={Style.rowDesc}>—</span>}</td>
                {BROWSERS.map((b) => {
                  const id = nativeIdFor(r, b.code);
                  return (
                    <td key={b.code}>
                      {id
                        ? <span className={Style.mono} title={id}>{id}</span>
                        // Not an error — plenty of extensions genuinely have no
                        // build for a given browser.
                        : <span className={Style.rowDesc} title="No build for this browser">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  catalogue: state.entities.dataControlEntity.extensionCatalogue,
  loading: state.entities.dataControlEntity.extensionCatalogueLoading,
});

export default connect(mapStateToProps)(Extensions);
